import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, catchError, throwError } from 'rxjs';
import { environment } from '../../environments/environment';

export interface EnisSyncParticipantLog {
  enisNumber: string;
  participantType: 'donor' | 'recipient';
  status: 'success' | 'failed' | 'updated' | 'created' | 'skipped';
  missingFields?: string[];
  errorMessage?: string;
  participantName?: string;
  center?: string;
  wasCreated?: boolean;
  wasUpdated?: boolean;
}

export interface EnisSyncLogRequest {
  fileName?: string;
  participants: EnisSyncParticipantLog[];
  generalErrors?: string;
}

export interface EnisSync {
  id: string;
  syncDate: Date;
  username: string;
  fileName?: string;
  totalParticipants: number;
  successfulImports: number;
  failedImports: number;
  updatedParticipants: number;
  createdParticipants: number;
  skippedParticipants: number;
  generalErrors?: string;
  participants: EnisSyncParticipant[];
}

export interface EnisSyncParticipant {
  id: string;
  enisSyncId: string;
  enisNumber: string;
  participantType: string;
  status: string;
  missingFields?: string;
  missingFieldsArray?: string[];
  errorMessage?: string;
  participantName?: string;
  center?: string;
  wasCreated: boolean;
  wasUpdated: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class EnisSyncService {
  private apiUrl = environment.apiUrl + 'enissync';

  constructor(private http: HttpClient) {}

  logEnisSync(logData: EnisSyncLogRequest): Observable<EnisSync> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post<EnisSync>(this.apiUrl + '/log', logData, { headers }).pipe(
      catchError((error) => {
        console.error('Error logging ENISSYNC:', error);
        return throwError(() => new Error('Failed to log ENISSYNC'));
      })
    );
  }

  getLatestEnisSync(): Observable<EnisSync> {
    return this.http.get<EnisSync>(this.apiUrl + '/latest').pipe(
      catchError((error) => {
        console.error('Error fetching latest ENISSYNC:', error);
        return throwError(() => new Error('Failed to fetch latest ENISSYNC'));
      })
    );
  }

  getEnisSyncById(id: string): Observable<EnisSync> {
    return this.http.get<EnisSync>(`${this.apiUrl}/${id}`).pipe(
      catchError((error) => {
        console.error('Error fetching ENISSYNC by ID:', error);
        return throwError(() => new Error('Failed to fetch ENISSYNC'));
      })
    );
  }

  getAllEnisSyncs(limit: number = 10): Observable<EnisSync[]> {
    return this.http.get<EnisSync[]>(`${this.apiUrl}?limit=${limit}`).pipe(
      catchError((error) => {
        console.error('Error fetching ENISSYNC operations:', error);
        return throwError(() => new Error('Failed to fetch ENISSYNC operations'));
      })
    );
  }
}
